/**
 * Der Rückstand: was das Gerät noch loswerden muss.
 *
 * ⚠️ **Warum es die Anzeige überhaupt gibt** (Vorgabe des Nutzers, 2026-09-22): in vier von
 * sechs Ausgängen einer Übertragung merkt der Prüfer heute **nichts**. Kein Netz, ein
 * abgelehnter Eintrag, ein unbekannter Typ — in allen Fällen bewertet er weiter, und die Zahl
 * der wartenden Belege steht nur im Einstellungsblatt, an das er im geführten Zugriff gar
 * nicht herankommt. Die Pille in der Kopfzeile ist die Gegenmaßnahme.
 *
 * Dieses Modul rechnet nur. Gezählt wird in `server/rueckstand.ts`, gezeigt in
 * `features/header/Rueckstandspille.tsx`.
 */

/** Die vier Zustände, die die Anzeige unterscheidet. */
export type Rueckstandsstand = 'leer' | 'wartet' | 'sendet' | 'steckt';

/** Welche Art eine offene Datei ist — für die Aufschlüsselung im Einstellungsdialog. */
export type Dateiart =
  | 'screenshot'
  | 'photo'
  | 'video'
  | 'audio'
  | 'handwriting'
  | 'annotation'
  | 'sonstige';

export const DATEIARTEN: readonly Dateiart[] = [
  'screenshot',
  'photo',
  'video',
  'audio',
  'handwriting',
  'annotation',
  'sonstige',
];

const ORDNERARTEN: readonly Dateiart[] = ['screenshot', 'photo', 'video', 'audio', 'handwriting'];

/**
 * Die Art aus dem Pfad (`logs/<Bezug>/<Art>/<Name>`, s. `platform/storage.ts::logFolder`).
 *
 * ⚠️ **Markierungen vor dem Ordner.** Die Ebene eines Fotos liegt im Foto-Ordner
 * (`…-overlay.svg`), die eines Videos im Video-Ordner (`…-annotations.json`); nach dem Ordner
 * allein zählten sie als Foto bzw. Video, und die Zahl der Aufnahmen stimmte nicht.
 */
export function artAusPfad(pfad: string): Dateiart {
  if (pfad.endsWith('-overlay.svg') || pfad.endsWith('-annotations.json')) return 'annotation';
  const ordner = pfad.split('/').slice(-2, -1)[0] as Dateiart | undefined;
  return ordner && ORDNERARTEN.includes(ordner) ? ordner : 'sonstige';
}

export const keineDateien = (): Record<Dateiart, number> =>
  Object.fromEntries(DATEIARTEN.map((a) => [a, 0])) as Record<Dateiart, number>;

export interface Rueckstandszahlen {
  /** Offene `ExamLog`-Zeilen (`transferred = 'N'`). */
  pakete: number;
  /** Offene Dateien: Aufnahmen, Bogenabzüge, Markierebenen. */
  dateien: number;
  /**
   * Dateien, deren Übertragung aufgegeben wurde.
   *
   * Sie warten auf nichts mehr: die Datei liegt weiter auf dem iPad, aber der Weg zum Server
   * ist zu. Wer das nicht sieht, hält sie für übertragen.
   */
  abgewiesen: number;
  /** Die offenen Dateien je Art; ihre Summe ist `dateien`. */
  jeArt: Record<Dateiart, number>;
}

export const LEERER_RUECKSTAND: Rueckstandszahlen = {
  pakete: 0,
  dateien: 0,
  abgewiesen: 0,
  jeArt: keineDateien(),
};

/**
 * Welchen Zustand die Anzeige zeigt.
 *
 * ⚠️ **Rot erst bei Aufgegebenem, nicht bei jedem verpassten Takt.** In einer belegten
 * Prüfungs-WLAN-Zelle scheitert ein Durchgang regelmäßig; stünde die Anzeige dann auf Alarm,
 * wäre sie den halben Prüfungstag rot und sagte nichts mehr. Rot heisst deshalb: hier hilft
 * kein Warten mehr, das muss jemand ansehen.
 *
 * ⚠️ **„sendet" schlägt alles.** Läuft gerade ein Durchgang, ist das die jüngere und
 * nützlichere Auskunft als die Menge — der Prüfer sieht, dass sich etwas bewegt.
 */
export function standAus(zahlen: Rueckstandszahlen, sendet: boolean): Rueckstandsstand {
  if (sendet) return 'sendet';
  if (zahlen.abgewiesen > 0) return 'steckt';
  if (zahlen.pakete > 0 || zahlen.dateien > 0) return 'wartet';
  return 'leer';
}

/**
 * Was in der Pille steht — eine Zahl je Art, nie eine Summe.
 *
 * Pakete und Dateien geraten aus verschiedenen Gründen ins Stocken (ein Protokolleintrag am
 * Vertrag, eine Datei an ihrer Grösse); eine gemeinsame Summe verschwiege genau das.
 *
 * ⚠️ **Beide Gleise zeigen beide Zahlen** (Richtigstellung des Nutzers, 2026-09-22). Bis dahin
 * stand hier, Classic schicke Dateien nur auf Anforderung — das galt für den damaligen
 * Quelltext, war aber nicht das gewollte Verhalten. Seither schiebt auch der Classic-Takt
 * offene Aufnahmen von selbst nach (`server/classic/anforderungen.ts::sendeOffeneDateien`),
 * und die Zahl bedeutet in beiden Gleisen dasselbe: vorhandene minus übertragene.
 */
export function anzeigeZahlen(
  zahlen: Rueckstandszahlen,
): { pakete: number; dateien: number } {
  return { pakete: zahlen.pakete, dateien: zahlen.dateien };
}

/** Ab wann nur noch `99+` im Kreis steht. */
const PLAKETTEN_GRENZE = 99;

/**
 * Was im Kreis über einem Zeichen steht (Vorgabe des Nutzers, 2026-09-26).
 *
 * Die Zahlen standen bis dahin als Ziffern neben den Zeichen in einer Pille. Der Kreis darüber
 * ist das Muster, das jeder vom iPhone kennt, und er kostet die halbe Breite — in der
 * Kopfzeile des iPad Air 2 ist das der Unterschied zwischen einem abgeschnittenen und einem
 * lesbaren Stationsnamen.
 *
 * ⚠️ **Leer heisst: kein Kreis, aber sehr wohl ein Zeichen.** Nur die Plakette verschwindet;
 * die beiden Zeichen bleiben gedimmt stehen. Eine Anzeige, die ganz verschwindet, verwechselt
 * man mit einer, die es nicht gibt — dieselbe Begründung, die schon den Zustand `leer` der
 * alten Pille sichtbar gelassen hat.
 *
 * ⚠️ **Die Obergrenze ist kein Schönheitsfehler.** Nach einem Vormittag ohne Netz sind
 * dreistellige Rückstände der Normalfall, und eine dritte Ziffer sprengt den Kreis. Die
 * **volle** Zahl steht weiter im Aufklapper und in der Beschriftung für Vorlesehilfen
 * (`t.backlog.label`) — verschwiegen wird sie nirgends, nur nicht im Kreis gezeigt.
 */
export function plakettenText(anzahl: number): string {
  if (anzahl <= 0) return '';
  return anzahl > PLAKETTEN_GRENZE ? String(PLAKETTEN_GRENZE) + '+' : String(anzahl);
}

/** Ab dieser Zahl ist eine Art im Einstellungsdialog rot statt gelb (Vorgabe des Nutzers, 2026-10-14). */
export const VIEL_RUECKSTAND = 5;

/** Farbstufe einer Kachel im Einstellungsdialog: grau bei 0, gelb bis 5, rot darüber. */
export function rueckstandsStufe(anzahl: number): 'null' | 'wenig' | 'viel' {
  if (anzahl <= 0) return 'null';
  return anzahl > VIEL_RUECKSTAND ? 'viel' : 'wenig';
}
